import { fieldAccess, type CustomFieldEntity } from '@crm/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Pencil } from 'lucide-react';
import { useMemo, useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { api, type FieldDefinitionView } from '../../lib/client.ts';
import { arrangeFields, fieldsQuery, layoutQuery } from '../../lib/custom-fields.ts';
import { useSession } from '../../lib/session.tsx';
import { cn } from '../../lib/utils.ts';
import { Button } from '../ui/button.tsx';
import { FieldCell } from './cell.tsx';
import { FieldEditor } from './editor.tsx';

/** A select's stored values are option values; a reader wants the option labels. */
function withLabels(value: unknown, def: FieldDefinitionView): unknown {
  const label = (v: unknown) => def.options?.find((o) => o.value === v)?.label ?? v;
  if (!def.options?.length) return value;
  return Array.isArray(value) ? value.map(label) : label(value);
}

/**
 * A record's custom fields, grouped by the organisation's layout for its kind (Settings →
 * Fields): the project's Overview card (TS-1756) and an expanded job's block. The API leaves
 * out fields hidden from the caller's role; a `read` field shows but is not editable. Edit opens
 * every editable field at once and Save sends only what changed in one PATCH (`save`), so it is
 * one audit row and one timeline line. Absent while `custom_fields.enabled` is off or no field
 * of the kind is defined.
 *
 * `frame`: `card` is a card of its own (a page section); `section` is a labelled block inside
 * another panel, headed like that panel's other blocks (the job panel's Shortlists and Notes).
 */
export function RecordCustomFields({
  entity,
  values: raw,
  canWrite,
  save: saveValues,
  onSaved,
  testId,
  frame = 'card',
}: {
  entity: CustomFieldEntity;
  values: Record<string, unknown> | undefined;
  canWrite: boolean;
  /** Writes the changed keys (`{ custom }` on the record's PATCH). */
  save: (custom: Record<string, unknown>) => Promise<unknown>;
  /** Re-reads the record once saved. */
  onSaved: () => Promise<void>;
  testId: string;
  frame?: 'card' | 'section';
}) {
  const session = useSession();
  const enabled = session.isEnabled('custom_fields.enabled');
  const defs = useQuery({
    queryKey: fieldsQuery(entity),
    queryFn: () => api.fieldDefinitions.list(entity),
    enabled,
  });
  const layout = useQuery({
    queryKey: layoutQuery(entity),
    queryFn: () => api.layouts.get(entity),
    enabled,
    staleTime: 60_000,
  });
  const [draft, setDraft] = useState<Record<string, unknown> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const values = useMemo(() => raw ?? {}, [raw]);
  const groups = useMemo(
    () => (layout.data && defs.data ? arrangeFields(layout.data.layout, defs.data, []) : []),
    [layout.data, defs.data],
  );
  const save = useMutation({
    mutationFn: saveValues,
    // Leave edit mode only once the record has been re-read: clearing the draft first showed
    // the old values for a round trip. The mutation stays pending until then, so Save is off.
    onSuccess: async () => {
      await onSaved();
      setDraft(null);
      setError(null);
    },
    onError: (err) => setError(describeError(err)),
  });

  if (!enabled || !defs.data?.length || !layout.data) return null;
  const role = session.me.organisation.role;
  const managesFields = session.can('field_definition.manage');
  const editable = (key: string) => {
    const def = defs.data.find((d) => d.key === key);
    return canWrite && def !== undefined && fieldAccess(def, role, managesFields) === 'edit';
  };
  const anyEditable = defs.data.some((d) => editable(d.key));
  const changed = draft
    ? Object.fromEntries(
        Object.entries(draft).filter(([k, v]) => JSON.stringify(v) !== JSON.stringify(values[k])),
      )
    : {};
  const card = frame === 'card';

  return (
    <div
      className={cn(card && 'bg-card border-border rounded-card shadow-card border p-5')}
      data-testid={testId}
    >
      <div className={cn('flex items-center justify-between gap-2', card ? 'mb-3' : 'mb-2')}>
        <h3
          className={
            card
              ? 'text-muted-foreground text-xs font-semibold'
              : 'text-muted-foreground text-xs font-bold tracking-[0.08em] uppercase'
          }
        >
          Custom fields
        </h3>
        {anyEditable && draft === null && (
          <Button size="sm" variant="outline" onClick={() => setDraft({ ...values })}>
            <Pencil /> Edit
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-4">
        {groups.map((g) => (
          <section key={g.name} data-testid="custom-field-group">
            <h4 className="mb-2 text-sm font-medium">{g.name}</h4>
            {/* 1 column on a phone, 2 from `sm`, 3 on an HD screen or larger (`xl`, 1280px). */}
            <dl
              className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2 xl:grid-cols-3"
              data-testid="custom-field-grid"
            >
              {g.fields.map((f) => {
                const def = defs.data.find((d) => `custom.${d.key}` === f);
                if (!def) return null;
                const id = `${testId}-${def.key}`;
                return (
                  <div key={f} className="flex flex-col gap-1" data-testid={`field-${f}`}>
                    <dt className="text-muted-foreground text-xs">
                      <label htmlFor={id}>{def.label}</label>
                    </dt>
                    <dd>
                      {draft !== null && editable(def.key) ? (
                        <FieldEditor
                          id={id}
                          type={def.type}
                          label={def.label}
                          options={def.options}
                          value={draft[def.key]}
                          onChange={(v) => setDraft({ ...draft, [def.key]: v })}
                        />
                      ) : (
                        <FieldCell type={def.type} value={withLabels(values[def.key], def)} />
                      )}
                    </dd>
                  </div>
                );
              })}
            </dl>
          </section>
        ))}
      </div>
      {error && (
        <p className="text-danger mt-3 text-sm" role="alert">
          {error}
        </p>
      )}
      {draft !== null && (
        <div className="mt-4 flex gap-2">
          <Button
            size="sm"
            disabled={Object.keys(changed).length === 0 || save.isPending}
            onClick={() => save.mutate(changed)}
          >
            Save
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setDraft(null);
              setError(null);
            }}
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
